import { useEffect, useMemo, useState } from 'react';
import { receiptsService } from '../../services/api';
import { formatMoney } from '../../utils/pricing';
import { todayBrisbane } from '../../utils/costs';
import { EXPENSE_CATEGORIES, LIFE_BASES } from '../../utils/businessCosts';
import './ReceiptForm.css';

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const LINE_KINDS = [
  { value: 'expense', label: 'Business expense' },
  { value: 'equipment', label: 'Equipment (goes on the register)' },
  { value: 'job_cost', label: 'For a job' }
];

let lineSeq = 0;
const blankLine = (kind = 'expense') => ({
  key: `l${++lineSeq}`, kind, description: '', amount: '', category: 'consumables',
  life_basis: 'hours', life_hours: '', life_years: '', salvage_value: '0',
  job_id: '', job_category_id: ''
});

/**
 * "Add from a receipt" (backlog #687, GID-Session-018): the docket's photo(s)
 * and header once — supplier, date, who paid, GST, what the paper says in
 * total — then every business item on it as its own line: a business
 * expense, a piece of equipment for the register, or a cost for a job. Saved
 * together in one request; the lines inherit the header. Anything personal on
 * the same docket is never entered: it is the unclaimed difference shown in
 * the footer.
 */
export default function ReceiptForm({ staff = [], jobs = [], jobCategories = [], defaultPaidBy = '', onSaved, onCancel }) {
  const [header, setHeader] = useState({ vendor: '', receipt_date: todayBrisbane(), paid_by: defaultPaidBy || '', includes_gst: true, docket_total: '', notes: '' });
  const [files, setFiles] = useState([]);
  const [lines, setLines] = useState([blankLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const setH = (k, v) => setHeader((h) => ({ ...h, [k]: v }));
  const setLine = (key, k, v) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, [k]: v } : l)));
  const removeLine = (key) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));
  const addLine = () => setLines((ls) => [...ls, blankLine(ls[ls.length - 1]?.kind || 'expense')]);

  // Photo previews — object URLs, released when the files change or the form goes
  const previews = useMemo(() => files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })), [files]);
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  function addFiles(list) {
    const next = [...files, ...[...list]].slice(0, 5);
    setFiles(next);
  }
  const dropFile = (i) => setFiles((fs) => fs.filter((_, j) => j !== i));

  const linesTotal = round2(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0));
  const docket = header.docket_total === '' ? null : Number(header.docket_total);
  const notClaimed = docket === null || !Number.isFinite(docket) ? null : round2(docket - linesTotal);

  function validate() {
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const at = `Line ${i + 1}`;
      if (!l.description.trim()) return `${at}: what was it?`;
      if (l.amount === '' || !(Number(l.amount) >= 0)) return `${at}: enter the amount`;
      if (l.kind === 'equipment') {
        if (l.life_basis === 'hours' && !(Number(l.life_hours) > 0)) return `${at}: how many hours of use will it give?`;
        if (l.life_basis === 'years' && !(Number(l.life_years) > 0)) return `${at}: how many years will it last?`;
      }
      if (l.kind === 'job_cost' && !l.job_id) return `${at}: choose the job`;
    }
    return '';
  }

  async function save() {
    if (busy) return;
    const problem = validate();
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    const payload = {
      header: {
        vendor: header.vendor, receipt_date: header.receipt_date, paid_by: header.paid_by || null,
        includes_gst: header.includes_gst, docket_total: header.docket_total === '' ? null : Number(header.docket_total), notes: header.notes
      },
      lines: lines.map((l) => {
        if (l.kind === 'expense') return { kind: 'expense', description: l.description, amount: Number(l.amount), category: l.category };
        if (l.kind === 'equipment') {
          return {
            kind: 'equipment', name: l.description, cost: Number(l.amount), salvage_value: Number(l.salvage_value) || 0, life_basis: l.life_basis,
            life_hours: l.life_basis === 'hours' ? Number(l.life_hours) : null, life_years: l.life_basis === 'years' ? Number(l.life_years) : null
          };
        }
        return { kind: 'job_cost', job_id: Number(l.job_id), category_id: l.job_category_id ? Number(l.job_category_id) : null, description: l.description, amount: Number(l.amount) };
      }),
      files
    };
    const r = await receiptsService.create(payload);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Could not save the receipt'); return; }
    onSaved?.(r);
  }

  return (
    <div className="rf" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.type !== 'file') { e.preventDefault(); save(); } }}>
      <div className="rf-head">
        <strong>Add from a receipt</strong>
        <span className="text-muted">One docket, every business item on it as its own line. Anything personal on the same docket is simply left off.</span>
      </div>

      {/* ---- The photo(s) ---- */}
      <div className="rf-photos">
        {previews.map((p, i) => (
          <span key={p.url} className="rf-photo">
            <img src={p.url} alt={p.name} />
            <button type="button" className="rf-photo-remove" aria-label={`Remove ${p.name}`} onClick={() => dropFile(i)}>×</button>
          </span>
        ))}
        {files.length < 5 && (
          <label className="rf-add-photo">
            <span>📷</span><span>{files.length ? 'Another page' : 'Receipt photo'}</span>
            <input type="file" accept="image/*" multiple hidden aria-label="Receipt photo" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </label>
        )}
        {files.length === 0 && <span className="text-muted rf-photo-hint">Add the photo now, or later from the row's 📎.</span>}
      </div>

      {/* ---- The header: entered once, inherited by every line ---- */}
      <div className="rf-row">
        <input aria-label="Supplier" className="rf-grow" placeholder="Supplier — e.g. Bunnings" value={header.vendor} onChange={(e) => setH('vendor', e.target.value)} />
        <label className="rf-label">Date<input aria-label="Receipt date" type="date" value={header.receipt_date} onChange={(e) => setH('receipt_date', e.target.value)} /></label>
        <select aria-label="Paid by" value={header.paid_by} onChange={(e) => setH('paid_by', e.target.value)} title="Who fronted the cash — every line on this receipt; feeds the Between us ledger">
          <option value="">Paid by… (optional)</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <label className="rf-label" title="What the paper says in total, personal items included — the lines below are the business part">Docket total (optional)
          <div className="rf-money"><span>$</span><input aria-label="Docket total" type="number" min="0" step="0.01" value={header.docket_total} onChange={(e) => setH('docket_total', e.target.value)} placeholder="0.00" /></div>
        </label>
        <label className="checkbox-label rf-gst"><input type="checkbox" checked={!!header.includes_gst} onChange={(e) => setH('includes_gst', e.target.checked)} /> Amounts include GST</label>
      </div>

      {/* ---- The lines ---- */}
      <div className="rf-lines">
        {lines.map((l, i) => (
          <div key={l.key} className="rf-line" data-line={i + 1}>
            <select aria-label={`Line ${i + 1} kind`} className="rf-kind" value={l.kind} onChange={(e) => setLine(l.key, 'kind', e.target.value)}>
              {LINE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
            {l.kind === 'expense' && (
              <select aria-label={`Line ${i + 1} category`} value={l.category} onChange={(e) => setLine(l.key, 'category', e.target.value)}>
                {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value} title={c.hint || ''}>{c.label}</option>)}
              </select>
            )}
            {l.kind === 'job_cost' && (
              <>
                <select aria-label={`Line ${i + 1} job`} value={l.job_id} onChange={(e) => setLine(l.key, 'job_id', e.target.value)}>
                  <option value="">Which job?</option>
                  {jobs.map((j) => <option key={j.id} value={j.id}>{`${j.job_number} · ${j.title || 'Untitled job'}${j.customer_name ? ` · ${j.customer_name}` : ''}`}</option>)}
                </select>
                <select aria-label={`Line ${i + 1} job category`} value={l.job_category_id} onChange={(e) => setLine(l.key, 'job_category_id', e.target.value)}>
                  <option value="">Category…</option>
                  {jobCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </>
            )}
            <input aria-label={`Line ${i + 1} description`} className="rf-grow" placeholder={l.kind === 'equipment' ? 'Name it — e.g. Turbo head for the Kärcher' : 'What was it?'} value={l.description} onChange={(e) => setLine(l.key, 'description', e.target.value)} />
            <div className="rf-money"><span>$</span><input aria-label={`Line ${i + 1} amount`} type="number" min="0" step="0.01" value={l.amount} onChange={(e) => setLine(l.key, 'amount', e.target.value)} placeholder="0.00" /></div>
            {l.kind === 'equipment' && (
              <>
                <select aria-label={`Line ${i + 1} life measured in`} value={l.life_basis} onChange={(e) => setLine(l.key, 'life_basis', e.target.value)}>
                  {LIFE_BASES.map((b) => <option key={b.value} value={b.value} title={b.hint}>{b.label}</option>)}
                </select>
                {l.life_basis === 'years'
                  ? <input aria-label={`Line ${i + 1} life in years`} className="rf-life" type="number" min="0" step="0.5" value={l.life_years} onChange={(e) => setLine(l.key, 'life_years', e.target.value)} placeholder="years" />
                  : <input aria-label={`Line ${i + 1} life in hours`} className="rf-life" type="number" min="0" step="10" value={l.life_hours} onChange={(e) => setLine(l.key, 'life_hours', e.target.value)} placeholder="hours of use" />}
              </>
            )}
            <button type="button" className="rf-icon rf-remove" aria-label={`Remove line ${i + 1}`} title="Remove this line" onClick={() => removeLine(l.key)} disabled={lines.length === 1}>×</button>
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline rf-add-line" onClick={addLine}>+ Another line</button>
      </div>

      {/* ---- Footer: the sums and the buttons ---- */}
      {error && <p className="form-error rf-error">{error}</p>}
      <div className="rf-foot">
        <span className="rf-sums">
          <span>Lines <strong>{formatMoney(linesTotal)}</strong></span>
          {notClaimed !== null && (notClaimed >= -0.004
            ? <span className="text-muted">Not claimed (personal) <strong>{formatMoney(notClaimed)}</strong></span>
            : <span className="form-error">The lines add up to {formatMoney(-notClaimed)} more than the docket</span>)}
        </span>
        <span className="rf-spacer" />
        <button type="button" className="btn btn-sm btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : `Save receipt (${lines.length} line${lines.length === 1 ? '' : 's'})`}</button>
      </div>
    </div>
  );
}
